import { useEffect, useState, type FC } from 'react';
import { DownOutlined, RightOutlined, PushpinOutlined, PushpinFilled } from '@ant-design/icons';
import { Popconfirm, Popover, Radio, Table, TablePaginationConfig } from 'antd';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { Link, Space, Text, Button } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';
import cx from 'classnames';
import he from 'he';

import { CallbackSource, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { CallbackDetails, LinkCallbackModal } from '.';
import { TaskDueDate } from 'components';
import styles from '../callbacks.module.scss';

const sourceLabels = {
  [CallbackSource.OWSale]: 'OW Sale',
  [CallbackSource.G2UWebsite]: 'G2U Website',
};

type Props = {
  callbacks?: JsonDataItem;
  isLoading?: boolean;
  onUpdate: () => void;
  onReopen: () => void;
  onLink: (payload: JsonDataItem) => void;
  onUnlink: (payload: JsonDataItem) => void;
  onUpdateDueDate: (payload: JsonDataItem) => void;
  onLock: (payload: JsonDataItem) => void;
  onUnlock: (payload: JsonDataItem) => void;
  onPin: ({ taskId }: { taskId: number }) => void;
  callbackType: string;
  expandedRowIds: number[];
  linkNewCustomerId: number;
  pagination: TablePaginationConfig;
  onFilter: (taskSource: CallbackSource | null) => void;
  onPaginationChange: (config: JsonDataItem) => void;
} & WithOptionalDataTest;

export const CallbacksTable: FC<Props> = observer((props) => {
  const {
    onReopen,
    expandedRowIds,
    linkNewCustomerId,
    onUpdate,
    onLink,
    onUnlink,
    onLock,
    onUnlock,
    onPin,
    onUpdateDueDate,
    onFilter,
    callbacks,
    isLoading,
    pagination,
    onPaginationChange,
  } = props;
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>();
  const [linkCallbackId, setLinkCallbackId] = useState<number>();

  const handleExpand = (expanded: boolean, record: JsonDataItem) => {
    setExpandedRowKeys(expanded ? [record.ID] : []);
  };

  useEffect(() => {
    expandedRowIds && setExpandedRowKeys(expandedRowIds);
  }, [expandedRowIds]);

  return (
    <>
      <Table
        loading={isLoading}
        locale={{
          emptyText: isLoading ? <div style={{ height: 100 }} /> : undefined,
        }}
        columns={[
          {
            title: 'Callback ID',
            dataIndex: 'ID',
            key: 'ID',
            render: (_text, record) => (
              <>
                {record.locked ? (
                  <PushpinFilled
                    className={cx(styles.callbacks__icon, styles.callbacks__icon_primary)}
                    onClick={() => onPin({ taskId: record.ID })}
                  />
                ) : (
                  <PushpinOutlined className={styles.callbacks__icon} onClick={() => onPin({ taskId: record.ID })} />
                )}{' '}
                {record.record_ID}
              </>
            ),
            width: 110,
          },
          {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
              <Space.$ direction={Space.Direction.Vertical} style={{ padding: 12 }}>
                <Radio.Group
                  onChange={(e) => {
                    setSelectedKeys([e.target.value]);
                  }}
                  value={selectedKeys[0]}
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <Radio key={CallbackSource.OWSale} value={CallbackSource.OWSale}>
                    OW Sale
                  </Radio>
                  <Radio key={CallbackSource.G2UWebsite} value={CallbackSource.G2UWebsite}>
                    G2U Website
                  </Radio>
                </Radio.Group>
                <Space.$>
                  <Button.$
                    type={Button.Type.Primary}
                    size={Button.Size.Small}
                    dataTestId="apply-filter-btn"
                    onClick={() => {
                      onFilter(selectedKeys[0] as CallbackSource);
                      confirm();
                    }}
                  >
                    Search
                  </Button.$>
                  <Button.$
                    size={Button.Size.Small}
                    dataTestId="reset-filter-btn"
                    onClick={() => {
                      clearFilters?.();
                      onFilter(null);
                      confirm({ closeDropdown: true });
                    }}
                  >
                    Reset
                  </Button.$>
                </Space.$>
              </Space.$>
            ),
            render: (_text, record) => <>{sourceLabels[record.record.source as CallbackSource]}</>,
            width: 120,
          },
          {
            title: 'Due Date & Time',
            dataIndex: 'due_date',
            key: 'dueDate',
            render: (text, record) => {
              return <TaskDueDate text={text} record={record} onDateUpdate={onUpdateDueDate} />;
            },
            width: 180,
          },
          {
            title: 'Linked Customer',
            key: 'linkedCustomer',
            render: (record) =>
              record.task_links?.customer ? (
                <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
                  <Space.$ direction={Space.Direction.Vertical} compact>
                    <span>{record.task_links?.customer?.name.full_name}</span>
                    <span>{record.task_links?.customer?.contact[0]}</span>
                    <span>{record.task_links?.customer?.email}</span>
                  </Space.$>
                  <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.Middle}>
                    <Popconfirm
                      title="Unlink to Customer"
                      disabled={!!record.archive_datetime}
                      onConfirm={() => {
                        onUnlink({ taskId: record.ID });
                      }}
                      description="Are you sure you want to unlink this callback from the customer?"
                      okText="Yes"
                    >
                      <div>
                        <Link.$ disabled={!!record.archive_datetime}>Unlink</Link.$>
                      </div>
                    </Popconfirm>
                    <Popover
                      content={
                        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
                          <Text.$ strong>
                            {record.task_links.customer.name.full_name} #{record.task_links.customer.ID}
                          </Text.$>
                          <Text.$>
                            <div>{record.task_links.customer.contact[0]}</div>
                            <div>{record.task_links.customer.email}</div>
                            <div>{record.task_links.customer.address.full_address}</div>
                          </Text.$>
                        </Space.$>
                      }
                    >
                      <div>
                        <Link.$>View Details</Link.$>
                      </div>
                    </Popover>
                  </Space.$>
                </Space.$>
              ) : (
                <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
                  <>Not Linked</>
                  <Link.$
                    onClick={() => {
                      setLinkCallbackId(record.ID);
                    }}
                    disabled={!!record.archive_datetime}
                  >
                    Link Customer
                  </Link.$>
                </Space.$>
              ),
          },
          {
            title: 'Assignee',
            key: 'assignee',
            render: (record) => he.decode(record.user?.name || '') || 'Unassigned',
            width: 170,
          },
        ]}
        dataSource={
          callbacks?.data
            ? [...callbacks.data].sort((a: JsonDataItem, b: JsonDataItem) => b.record_ID - a.record_ID)
            : undefined
        }
        pagination={pagination}
        onChange={(rawPagination) => {
          onPaginationChange({
            page: rawPagination.current,
            pageSize: rawPagination.pageSize,
          });
        }}
        expandable={{
          onExpand: handleExpand,
          expandedRowKeys: expandedRowKeys as any,
          expandedRowRender: (record) => {
            return expandedRowKeys?.includes(record.ID) ? (
              <CallbackDetails
                page={pagination.current}
                linkNewCustomerId={linkNewCustomerId}
                record={record}
                onReopen={onReopen}
                onLink={onLink}
                onClose={onUpdate}
                onLock={onLock}
                onUnlock={onUnlock}
              />
            ) : (
              <></>
            );
          },
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <DownOutlined onClick={(e) => onExpand(record, e)} />
            ) : (
              <RightOutlined onClick={(e) => onExpand(record, e)} />
            ),
        }}
        rowKey="ID"
        className={styles.callbacks__table}
      />
      {!!linkCallbackId && (
        <LinkCallbackModal
          isOpen={!!linkCallbackId}
          callbackId={linkCallbackId}
          onLink={(payload) => onLink({ taskId: linkCallbackId, ...payload })}
          onFinish={() => setLinkCallbackId(0)}
          page={pagination.current}
        />
      )}
    </>
  );
});
